import { FC, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useAsync } from 'react-use';
import { Checkbox, CircularProgress } from '@getgo/chameleon-web-react-wrapper';

import { useAppDispatch, useAppSelector } from 'hooks';
import useProvisioning from 'hooks/useProvisioning';
import { AdyenWrapper, loadAdyenCCCheckout } from 'lib/adyen-client';
import { showErrorSnack } from 'modules/error';
import { accountKey, locale } from 'modules/global-wrapper';
import { isPaymentMethodPresent } from 'modules/payment-methods';
import { isNewTabPayment, sessionInvoiceNumber, sessionSuccessRedirectUrl } from 'modules/session-details';
import Track, { OnePayEventNames, PaymentMethodEventNames } from 'modules/tracking';
import {
  postTransactionAdyenFinalize,
  postTransactionAdyenPrepare,
  transactionsAdyenLoading,
} from 'modules/transaction-adyen';
import { FinalizeAdyenResponse, PrepareAdyenResp } from 'types/transaction-adyen';
import {
  COPAS_PAYMENT_STATUS,
  CREDIT_CARD_ADD_PAY,
  PAYMENT_METHODS_TYPES,
  SEARCH_PARAMS,
  THEMES,
  TRACKING_EVENT_STATUS,
} from 'utils/constants';
import st from 'utils/shared-translations';

import './adyen-credit-card-form.css';

interface AdyenCreditCardFormProps {
  intent: string;
}

let AdyenClient: AdyenWrapper | undefined = undefined;
let card: any = undefined;

const loadExternalScripts = async (prepareResp: PrepareAdyenResp) => {
  const { adyenJsSettings } = prepareResp;
  AdyenClient = await loadAdyenCCCheckout(adyenJsSettings);
};

const AdyenCreditCardForm: FC<AdyenCreditCardFormProps> = ({ intent }) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const [isLoading, setLoading] = useState(true);
  const [isDefaultDisabled, setDefaultDisabled] = useState(true);
  const isDefaultPaymentMethod = useRef(false);

  const { processQuote, processRenewal, processInvoice } = useProvisioning();

  const selectedGlobalAccountKey = useAppSelector(accountKey);
  const selectedGlobalLocale = useAppSelector(locale);

  const renewalDate = localStorage.getItem(SEARCH_PARAMS.renewalDate) || '';
  const globalTheme = localStorage.getItem(SEARCH_PARAMS.theme);

  // Session details selectors
  const selectedSuccessRedirectUrl = useAppSelector(sessionSuccessRedirectUrl);
  const selectedIsNewTabPayment = useAppSelector(isNewTabPayment);

  const selectedAdyenIsLoading = useAppSelector(transactionsAdyenLoading);

  const selectedInvoiceNumber = useAppSelector(sessionInvoiceNumber);

  const selectedPaymentMethodIsPresent = useAppSelector(isPaymentMethodPresent);

  const onPaymentCompleted = async (result, component, prepareResp) => {
    component.unmount();
    try {
      const response: FinalizeAdyenResponse = await dispatch(
        postTransactionAdyenFinalize({
          checkoutSessionId: prepareResp.adyenSessionId,
          checkoutSessionResult: result.sessionResult,
          setAsDefault: isDefaultPaymentMethod.current,
          paymentMethod: 'cca',
        }),
      ).unwrap();
      Track(OnePayEventNames.adyenCCPaymentCompleted, { status: response.status });
      if (response.status !== COPAS_PAYMENT_STATUS.SUCCESS) {
        throw new Error('Adyen finalization failed');
      } else if (intent === CREDIT_CARD_ADD_PAY) {
        if (selectedIsNewTabPayment) window.close();
        else if (renewalDate) processRenewal(PAYMENT_METHODS_TYPES.ccAdyen);
        else if (selectedInvoiceNumber) processInvoice(PAYMENT_METHODS_TYPES.ccAdyen);
        else processQuote(PAYMENT_METHODS_TYPES.ccAdyen);
      } else {
        window.location.assign(selectedSuccessRedirectUrl);
      }
    } catch {
      Track(PaymentMethodEventNames.creditCardSaveCTA, { status: TRACKING_EVENT_STATUS.copasFailure });
      dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
    }
  };

  const onError = (error) => {
    dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
    Track(OnePayEventNames.adyenCCFormError, { error });
  };

  const onPaymentFailed = (error) => {
    dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
    Track(OnePayEventNames.adyenCCPaymentFailed, { error });
  };

  const initializeAdyenCard = async (prepareResp: PrepareAdyenResp) => {
    const configuration = {
      environment: prepareResp.adyenEnvironment,
      clientKey: prepareResp.adyenClientKey,
      session: {
        id: prepareResp.adyenSessionId,
        sessionData: prepareResp.adyenSessionData,
      },
      onPaymentCompleted: (result, component) => onPaymentCompleted(result, component, prepareResp),
      onError,
      onPaymentFailed,
    };
    const paymentMethodOptions = {
      onConfigSuccess: () => {
        setDefaultDisabled(false);
      },
    };

    try {
      if (AdyenClient) {
        card = await AdyenClient.initialize(configuration, paymentMethodOptions);
        setLoading(false);
        card.mount('#adyen-credit-card-form');
      }
    } catch {
      dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
    }
  };

  useAsync(async () => {
    if (selectedGlobalAccountKey) {
      try {
        const resp: PrepareAdyenResp = await dispatch(
          postTransactionAdyenPrepare({
            paymentMethodKey: null,
            browserLocale: selectedGlobalLocale,
            returnUrl: window.location.origin,
            paymentMethod: 'cca',
          }),
        ).unwrap();
        await loadExternalScripts(resp);
        initializeAdyenCard(resp);
      } catch {
        Track(PaymentMethodEventNames.prepareCCResponse, { status: TRACKING_EVENT_STATUS.copasFailure });
        dispatch(showErrorSnack(st['alert.error.general.refreshtryagain']));
      }
    }
  }, [selectedGlobalAccountKey]);

  return (
    <>
      <div
        id="adyen-credit-card-form"
        className={globalTheme?.toUpperCase() === THEMES.dark ? 'adyen-credit-card-dark-mode' : ''}
      ></div>
      {(isLoading || selectedAdyenIsLoading) && (
        <div className="adyen-credit-card-form__loader">
          <CircularProgress />
        </div>
      )}
      {!isLoading && selectedPaymentMethodIsPresent && (
        <section className="adyen-credit-card-form__set-default">
          <Checkbox onChange={(e) => (isDefaultPaymentMethod.current = e.target.checked)} disabled={isDefaultDisabled}>
            {intl.formatMessage(st['payment.method.set.as.default'])}
          </Checkbox>
        </section>
      )}
    </>
  );
};

export default AdyenCreditCardForm;
